"use client"

import { useState } from "react"
import "./Sales.css" // keep styles

// expects apiBase prop (string) and documents + onDocumentsChange
const DocumentUpload = ({ apiBase = "/api", documents = [], onDocumentsChange }) => {
  const [dragActive, setDragActive] = useState(false)
  const [uploadingFiles, setUploadingFiles] = useState([]) // track uploading filenames

  const documentTypes = [
    { value: "passport", label: "Passport" },
    { value: "visa", label: "Visa" },
    { value: "crs", label: "CRS Document" },
    { value: "invoice", label: "Invoice" },
    { value: "receipt", label: "Receipt" },
    { value: "insurance", label: "Insurance" },
    { value: "ticket", label: "Ticket" },
    { value: "hotel_voucher", label: "Hotel Voucher" },
    { value: "other", label: "Other" },
  ]

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
  }

  const getFileIcon = (mimeType) => {
    if (!mimeType) return "📎"
    if (mimeType.startsWith("image/")) return "🖼️"
    if (mimeType.includes("pdf")) return "📄"
    if (mimeType.includes("word")) return "📝"
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) return "📊"
    return "📎"
  }

  const uploadSingleFile = async (file, tempId) => {
    const filename = file.name
    setUploadingFiles((s) => [...s, filename])

    const fd = new FormData()
    fd.append("file", file)

    try {
      const res = await fetch(`${apiBase}/api/documents/upload`, {
        method: "POST",
        body: fd,
      })

      if (!res.ok) {
        const txt = await res.text().catch(() => "")
        throw new Error(`Status ${res.status} ${txt}`)
      }

      const data = await res.json()
      const url = data.file_url || data.url || data.file || data.file_path || null

      onDocumentsChange((prev) =>
        prev.map((doc) =>
          doc.id === tempId
            ? {
                ...doc,
                status: "uploaded",
                url: url,
                file_path: url,
              }
            : doc
        )
      )
    } catch (err) {
      console.error("Upload failed:", err)
      alert(`Failed to upload ${filename}`)
      onDocumentsChange((prev) =>
        prev.map((doc) =>
          doc.id === tempId
            ? {
                ...doc,
                status: "failed",
              }
            : doc
        )
      )
    } finally {
      setUploadingFiles((s) => s.filter((n) => n !== filename))
    }
  }

  const handleFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return
    const files = Array.from(fileList)

    const uploadTasks = files.map((file) => ({
      tempId: Date.now() + Math.random(),
      file,
    }))

    // create pending doc previews immediately
    const previewDocs = uploadTasks.map(({ tempId, file }) => ({
      id: tempId,
      document_type: "other",
      document_name: file.name,
      file_size: file.size,
      mime_type: file.type,
      uploaded_at: new Date().toISOString(),
      status: "uploading",
      url: null,
      file_path: null,
    }))

    onDocumentsChange((prev) => [...prev, ...previewDocs])

    // upload files in parallel
    uploadTasks.forEach(({ tempId, file }) => {
      uploadSingleFile(file, tempId)
    })
  }

  return (
    <div className="document-upload">
      <h4>Document Management</h4>

      <div
        className={`upload-zone ${dragActive ? "drag-active" : ""}`}
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true) }}
        onDragLeave={(e) => { e.preventDefault(); setDragActive(false) }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFiles(e.dataTransfer.files) }}
      >
        <div className="upload-content">
          <div className="upload-icon">📁</div>
          <p>Drag and drop files here, or click to select</p>
          <input id="file-input" type="file" multiple hidden onChange={(e) => handleFiles(e.target.files)} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xlsx,.xls" />
          <label htmlFor="file-input" className="btn-primary" style={{ cursor: "pointer" }}>Choose Files</label>
        </div>
      </div>

      {uploadingFiles.length > 0 && (
        <div className="upload-progress">
          <p>Uploading: {uploadingFiles.join(", ")}</p>
        </div>
      )}

      {documents.length > 0 && (
        <div className="documents-list">
          <h5>Documents ({documents.length})</h5>
          <div className="documents-grid">
            {documents.map((doc) => (
              <div key={doc.id} className="document-card">
                <div className="document-header" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span className="file-icon">{getFileIcon(doc.mime_type || "")}</span>
                  <div style={{ flex: 1 }}>
                    <div className="document-name" title={doc.document_name}>{doc.document_name}</div>
                    <div className="document-size">{formatFileSize(doc.file_size)}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {(doc.url || doc.file_path) ? (
                      <a
                        href={doc.url || doc.file_path}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-primary"
                        style={{
                          padding: "5px 10px",
                          fontSize: "0.75rem",
                          borderRadius: "6px",
                          textDecoration: "none",
                          lineHeight: "1.2",
                        }}
                      >
                        View
                      </a>
                    ) : (
                      <span style={{ color: "#6b7280", fontSize: "0.75rem" }}>
                        No URL
                      </span>
                    )}
                    <button
                      className="remove-btn"
                      onClick={() =>
                        onDocumentsChange((prev) =>
                          prev.filter((d) => d.id !== doc.id)
                        )
                      }
                    >
                      ×
                    </button>
                  </div>
                </div>

                <div className="document-body">
                  <div className="form-group">
                    <label>Document Type</label>
                    <select value={doc.document_type} onChange={(e) => {
                      const newType = e.target.value
                      onDocumentsChange((prev) => prev.map(d => d.id === doc.id ? { ...d, document_type: newType } : d))
                    }}>
                      {documentTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>

                  <div className="document-status">
                    <span className={`status-badge status-${doc.status}`}>
                      {doc.status === "uploading" ? "Uploading..." : doc.status === "uploaded" ? "Uploaded" : doc.status === "failed" ? "Failed" : "Pending"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default DocumentUpload
